import { z } from 'zod';
import { uuidSchema } from './validation.js';
/**
 * BOM (Bill of Materials) — sku_components schemas (BLU-534).
 *
 * A component link attaches a trim SKU to a parent (a product, applying to
 * every variant, OR a single SKU) with a quantity-per-unit + unit of measure.
 * Stock is integer, so fractional totals are rounded at deduction time.
 */
export const UNIT_OF_MEASURE_VALUES = ['pcs', 'm', 'cm', 'g', 'kg'];
export const unitOfMeasureSchema = z.enum(UNIT_OF_MEASURE_VALUES);
/**
 * BLU-534 gap-fill: WHEN a component is consumed.
 * 'order' (default) — fulfilment-time (POD: the trim is attached when we
 *   print/pack). 'receipt' — PO-receipt-time (cut & sew: the factory already
 *   consumed the trim before the finished goods arrive). Each deduction
 *   engine filters to its own stage only — see bom-deduction.ts.
 */
export const DEDUCT_AT_VALUES = ['order', 'receipt'];
export const deductAtSchema = z.enum(DEDUCT_AT_VALUES);
/** List component links for a parent product / SKU, or all parents of a component. Exactly one filter. */
export const listSkuComponentsSchema = z
    .object({
    parentProductId: uuidSchema.optional(),
    parentSkuId: uuidSchema.optional(),
    componentSkuId: uuidSchema.optional(),
})
    .refine((v) => [v.parentProductId, v.parentSkuId, v.componentSkuId].filter(Boolean).length === 1, { message: 'Provide exactly one of parentProductId, parentSkuId, componentSkuId' });
export const upsertSkuComponentSchema = z
    .object({
    /** Present when editing an existing link. */
    id: uuidSchema.optional(),
    parentProductId: uuidSchema.optional(),
    parentSkuId: uuidSchema.optional(),
    componentSkuId: uuidSchema,
    quantityPerUnit: z.number().positive().max(999999),
    unitOfMeasure: unitOfMeasureSchema.default('pcs'),
    deductAt: deductAtSchema.default('order'),
})
    .refine((v) => [v.parentProductId, v.parentSkuId].filter(Boolean).length === 1, {
    message: 'Provide exactly one of parentProductId, parentSkuId',
});
export const deleteSkuComponentSchema = z.object({ id: uuidSchema });
/** Link one trim to many products in one call (the "used everywhere" flow). */
export const bulkLinkComponentSchema = z.object({
    componentSkuId: uuidSchema,
    parentProductIds: z.array(uuidSchema).min(1).max(500),
    quantityPerUnit: z.number().positive().max(999999).default(1),
    unitOfMeasure: unitOfMeasureSchema.default('pcs'),
    deductAt: deductAtSchema.default('order'),
});
